import { datadogRum } from '@datadog/browser-rum'
import config from 'config'
import AssetType from 'state/enums/AssetType'
import { User } from 'state/user/UserState'

import { OctaneLogger as logger } from './OctaneLogger'

export default class DataDogRUM {
    allowedEnvironmentList = ['production']
    private isEnabled = false

    private productionAutoUrls = ['neko.octane.co', 'allamerican.octane.co']

    constructor() {
        if (!this.allowedEnvironmentList.includes(config.ENVIRONMENT)) {
            this.isEnabled = false

            // Ensure DD_RUM_ENABLED is never set in non-production environments
            window.DD_RUM_ENABLED = false

            logger.info(
                `DataDog RUM disabled for environment: ${config.ENVIRONMENT}`,
            )

            return
        }

        this.isEnabled = true
        try {
            // Auto-branded URLs are part of the existing automobile experience and
            // are always enabled. Pre-login sessions on other URLs are intentionally
            // not initialized: RUM is restricted to automobile dealerships.
            const isUrlBasedAuto = this.isAutoBrandedExperienceByUrl()

            if (isUrlBasedAuto) {
                this.initializeDataDogRUM()

                // Set context property to indicate URL-based detection
                if (window.DD_RUM_ENABLED) {
                    datadogRum.setGlobalContextProperty(
                        'auto_detection_method',
                        'url_based',
                    )

                    datadogRum.setGlobalContextProperty(
                        'is_auto_branded_experience',
                        true,
                    )
                }
            }
        } catch (error) {
            // Silently handle errors to prevent interference with application functionality
            logger.error(
                'DataDog RUM constructor error',
                undefined,
                error as Error,
            )
        }
    }

    private isAutoBrandedExperienceByUrl(): boolean {
        if (!this.isEnabled) return false

        const currentHostname = window.location.hostname

        return this.productionAutoUrls.some((autoUrl) =>
            currentHostname.includes(autoUrl),
        )
    }

    private initializeDataDogRUM() {
        if (!this.isEnabled) return

        try {
            if (!window.DD_RUM_ENABLED) {
                datadogRum.init({
                    clientToken: 'pubb0c7be21e79ad96ed7ac545e3c83238d',
                    applicationId: '082d131f-ee5b-407c-8a33-a7a2f22753c9',
                    site: 'datadoghq.com',
                    service: process.env.REACT_APP_NAME,
                    version: process.env.REACT_APP_RELEASE_VERSION,
                    env: config.ENVIRONMENT,
                    sessionSampleRate: 100,
                    trackUserInteractions: true,
                    trackViewsManually: true,
                    trackResources: true,
                    trackLongTasks: true,
                    silentMultipleInit: true,
                    allowFallbackToLocalStorage: true,
                    storeContextsAcrossPages: true,
                    sessionReplaySampleRate: 100,
                    defaultPrivacyLevel: 'mask-user-input',
                    // options that must match the DD Logs config
                    trackSessionAcrossSubdomains: true,
                    useSecureSessionCookie: true,
                    usePartitionedCrossSiteSessionCookie: true,
                    // Tracing
                    allowedTracingUrls: [config.API_BASE_URL],
                })

                window.DD_RUM_ENABLED = true
            }
        } catch (error) {
            // Silently handle errors to prevent interference with application functionality
            logger.error(
                'DataDog RUM initialization error',
                undefined,
                error as Error,
            )
        }
    }

    setUser(user: User) {
        if (!this.isEnabled) return

        try {
            const dealership = user?.dealership
            const primaryAssetType = dealership?.primaryAssetType

            // Datadog RUM is restricted to automobile dealerships. Stop any
            // pre-login session for non-automobile dealerships so we don't
            // send authenticated data for users outside the intended audience.
            if (primaryAssetType !== AssetType.AUTOMOBILE) {
                if (window.DD_RUM_ENABLED) {
                    datadogRum.stopSession()
                    window.DD_RUM_ENABLED = false
                }

                return
            }

            if (!window.DD_RUM_ENABLED) {
                this.initializeDataDogRUM()

                // Mark that detection was done via user login
                datadogRum.setGlobalContextProperty(
                    'auto_detection_method',
                    'user_login',
                )
            }

            datadogRum.setUser({
                id: user?.id?.toString(),
                name: [user?.firstName, user?.lastName].join(' '),
                email: user?.email,
                username: user?.username,
            })

            const dealershipId = dealership?.id
            const dealershipName = dealership?.name

            if (dealershipId) {
                datadogRum.setGlobalContextProperty('dealership', {
                    id: dealershipId,
                    name: dealershipName,
                })
            }

            datadogRum.setGlobalContextProperty(
                'primaryAssetType',
                primaryAssetType,
            )
        } catch (error) {
            // Silently handle errors to prevent interference with application functionality
            logger.error('DataDog RUM setUser error', undefined, error as Error)
        }
    }

    clearUser() {
        if (!this.isEnabled) return

        try {
            if (window.DD_RUM_ENABLED) {
                datadogRum.clearUser()
            }
        } catch (error) {
            logger.error(
                'DataDog RUM clearUser error',
                undefined,
                error as Error,
            )
        }
    }

    startView(path: string) {
        if (!this.isEnabled) return

        try {
            if (window.DD_RUM_ENABLED) {
                datadogRum.startView(path)
            }
        } catch (error) {
            logger.error(
                'DataDog RUM startView error',
                undefined,
                error as Error,
            )
        }
    }
}
